import { cn } from '@/lib/cn'
import { useLogout, useMe } from '@/api/hooks'
import { Button } from '@/components/ui/Button'
import { PageLoader } from '@/components/ui/Feedback'
import type { AccountUser } from '@/api/types'
import {
  ChefHat,
  LogOut,
  Carrot,
  ShieldCheck,
  User as UserIcon,
  UtensilsCrossed,
} from 'lucide-react'
import { useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'

interface NavItem {
  to: string
  label: string
  icon: typeof ChefHat
  adminOnly?: boolean
}

/**
 * Sidebar links always point at a route root, never at "whatever you were last
 * looking at". That was the Streamlit behaviour this replaces.
 */
const NAV_ITEMS: NavItem[] = [
  { to: '/recommendations', label: 'Recommend', icon: UtensilsCrossed },
  { to: '/recipes', label: 'Recipes', icon: ChefHat },
  { to: '/ingredients', label: 'Ingredients', icon: Carrot },
  { to: '/admin/users', label: 'Users', icon: ShieldCheck, adminOnly: true },
]

function UserBadge({ user }: { user: AccountUser }) {
  const initial = (user.display_name || user.username).charAt(0).toUpperCase()
  return (
    <Link
      to="/account"
      className="flex min-w-0 items-center gap-2.5 rounded-[var(--radius-field)] px-2 py-1.5 transition-colors hover:bg-surface"
    >
      <span
        aria-hidden
        className="grid size-8 shrink-0 place-items-center rounded-full bg-accent-soft text-xs font-semibold text-accent-ink"
      >
        {initial}
      </span>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-ink">{user.display_name}</p>
        <p className="truncate text-xs text-ink-faint">
          {user.role === 'admin' ? 'Admin' : 'Member'}
        </p>
      </div>
    </Link>
  )
}

export function AppLayout() {
  const me = useMe()
  const logout = useLogout()
  const navigate = useNavigate()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)

  if (me.isPending) {
    return <PageLoader label="Starting…" />
  }

  if (me.isError) {
    // /auth/me answers 401 when there is no valid session. The login page
    // decides between signing in and first-run setup.
    navigate('/login', { replace: true })
    return null
  }

  const user = me.data
  if (!user) {
    navigate('/login', { replace: true })
    return null
  }

  const items = NAV_ITEMS.filter((item) => !item.adminOnly || user.role === 'admin')

  async function signOut() {
    try {
      await logout.mutateAsync()
    } finally {
      window.location.assign('/login')
    }
  }

  return (
    <div className="flex min-h-dvh flex-col bg-canvas md:flex-row">
      {/* Desktop sidebar. It owns its own scroll position, so the page content
          scrolls independently behind it. */}
      <aside className="sticky top-0 hidden h-dvh w-60 shrink-0 flex-col border-r border-line/60 bg-canvas md:flex">
        <Link
          to="/recipes"
          className="flex items-center gap-2.5 px-5 pt-5 pb-4"
        >
          <span
            aria-hidden
            className="grid size-8 shrink-0 place-items-center rounded-[var(--radius-field)] bg-accent text-on-accent"
          >
            <ChefHat size={17} />
          </span>
          <span className="truncate text-[0.9375rem] font-semibold tracking-tight text-ink">
            Meal Planner
          </span>
        </Link>

        <nav className="flex-1 space-y-0.5 px-3 py-2">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-[var(--radius-field)] px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-accent-soft text-accent-ink'
                    : 'text-ink-soft hover:bg-surface hover:text-ink',
                )
              }
            >
              <item.icon size={16} aria-hidden />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="space-y-1 p-3">
          {user.must_change_password ? <ChangePasswordPrompt /> : null}
          <UserBadge user={user} />
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start px-3"
            loading={logout.isPending}
            onClick={() => void signOut()}
          >
            <LogOut size={14} aria-hidden />
            Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile header */}
      <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-line bg-canvas/90 px-4 py-2.5 backdrop-blur-md md:hidden">
        <Link to="/recipes" className="flex min-w-0 items-center gap-2">
          <span
            aria-hidden
            className="grid size-7 shrink-0 place-items-center rounded-md bg-accent text-on-accent"
          >
            <ChefHat size={15} />
          </span>
          <span className="truncate text-sm font-semibold tracking-tight">Meal Planner</span>
        </Link>
        <div className="flex shrink-0 items-center gap-1.5">
          {user.must_change_password ? (
            <Link
              to="/account"
              className="rounded-full bg-caution-soft px-2.5 py-1 text-xs font-medium text-caution"
            >
              Set password
            </Link>
          ) : null}
          <Button variant="ghost" size="icon-sm" onClick={() => setMenuOpen(true)} aria-label="Open menu">
            <UserIcon size={18} />
          </Button>
        </div>
      </header>

      {/* Mobile menu */}
      {menuOpen ? (
        <div className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            aria-label="Close menu"
            className="absolute inset-0 bg-ink/50"
            onClick={() => setMenuOpen(false)}
          />
          <div className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-surface p-3 pb-6 shadow-overlay">
            <nav className="space-y-0.5">
              {items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2.5 rounded-[var(--radius-field)] px-3 py-2.5 text-sm font-medium',
                      isActive ? 'bg-accent-soft text-accent-ink' : 'text-ink-soft',
                    )
                  }
                >
                  <item.icon size={16} aria-hidden />
                  {item.label}
                </NavLink>
              ))}
              <NavLink
                to="/account"
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2.5 rounded-[var(--radius-field)] px-3 py-2.5 text-sm font-medium',
                    isActive ? 'bg-accent-soft text-accent-ink' : 'text-ink-soft',
                  )
                }
              >
                <UserIcon size={16} aria-hidden />
                Your account
              </NavLink>
            </nav>
            <div className="mt-3 space-y-2 border-t border-line pt-3">
              {user.must_change_password ? <ChangePasswordPrompt /> : null}
              <UserBadge user={user} />
              <Button variant="secondary" size="sm" className="w-full" onClick={() => void signOut()}>
                <LogOut size={14} aria-hidden />
                Sign out
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <main className="min-w-0 flex-1 pb-16 md:pb-0" key={location.pathname}>
        <Outlet />
      </main>

      {/* Mobile bottom navigation */}
      <nav className="fixed inset-x-0 bottom-0 z-30 flex h-16 border-t border-line bg-surface/95 backdrop-blur-md md:hidden">
        {items.slice(0, 3).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                'group flex flex-1 flex-col items-center justify-center gap-0.5 text-xs font-medium transition-colors',
                isActive ? 'text-accent-ink' : 'text-ink-faint',
              )
            }
          >
            {({ isActive }) => (
              <>
                <span
                  className={cn(
                    'grid h-7 w-12 place-items-center rounded-full transition-colors',
                    isActive && 'bg-accent-soft',
                  )}
                >
                  <item.icon size={18} aria-hidden />
                </span>
                {item.label}
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  )
}

function ChangePasswordPrompt() {
  return (
    <Link
      to="/account"
      className="block rounded-[var(--radius-field)] bg-caution-soft px-3 py-2 text-xs text-pretty text-caution hover:bg-caution/15"
    >
      Set a new password before continuing.
    </Link>
  )
}
